import { AnimatePresence, motion } from 'framer-motion'
import { Box, Spinner, Text } from '@chakra-ui/react'

import { TeamMember } from '@pkg/types/team'

import { TeamMemberItem } from '../TeamMemberItem'

interface SearchResultsDropdownProps {
  showResults: boolean
  dropdownRef: React.RefObject<HTMLDivElement | null>
  isLoadingMembers: boolean
  filteredMembers: TeamMember[]
  availableMembers: TeamMember[]
  searchQuery: string
  onMemberSelect: (memberId: string) => void
}

export const SearchResultsDropdown = ({
  showResults,
  dropdownRef,
  isLoadingMembers,
  filteredMembers,
  availableMembers,
  searchQuery,
  onMemberSelect,
}: SearchResultsDropdownProps) => (
  <AnimatePresence>
    {showResults && (
      <motion.div
        ref={dropdownRef}
        id="team-members-dropdown"
        role="listbox"
        aria-label="Team members list"
        initial={{ opacity: 0, y: -10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -5, scale: 0.98 }}
        transition={{
          duration: 0.15,
          ease: 'easeOut',
          exit: { duration: 0.1 },
        }}
        style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 1000,
          marginTop: '0.5rem',
          backgroundColor: 'white',
          borderRadius: '1.25rem',
          boxShadow: '0px 4px 30px 0px rgba(0, 0, 0, 0.25)',
          maxHeight: '20rem',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {isLoadingMembers ? (
          <Box px={4} py={2.5} _first={{ pt: 3.5 }} _last={{ pb: 3.5 }}>
            <Box display="flex" alignItems="center">
              <Spinner size="sm" mr={2} />
              <Text fontWeight="bold">Loading team members...</Text>
            </Box>
          </Box>
        ) : filteredMembers.length > 0 ? (
          filteredMembers.map((member, index) => (
            <TeamMemberItem
              key={member.email}
              member={member}
              onSelect={onMemberSelect}
              isFirst={index === 0}
              isLast={index === filteredMembers.length - 1}
            />
          ))
        ) : searchQuery.trim() ? (
          <Box
            px={4}
            py={2.5}
            _first={{ pt: 3.5 }}
            _last={{ pb: 3.5 }}
            color="gray.500"
            fontWeight="bold"
          >
            No team members found
          </Box>
        ) : availableMembers.length === 0 ? (
          <Box
            px={4}
            py={2.5}
            _first={{ pt: 3.5 }}
            _last={{ pb: 3.5 }}
            color="gray.500"
            fontWeight="bold"
          >
            All team members are already added
          </Box>
        ) : null}
      </motion.div>
    )}
  </AnimatePresence>
)
